import React from 'react';
import { motion } from 'framer-motion';
import { 
  FileImage, 
  FileVideo, 
  Folder, 
  BarChart3,
  Calendar,
  ArrowRight,
  Star,
  Users
} from 'lucide-react';
import { SearchResult } from '../../lib/global-search';

interface SearchResultItemProps {
  result: SearchResult;
  onClick: (result: SearchResult) => void;
  isHighlighted?: boolean;
}

export function SearchResultItem({ result, onClick, isHighlighted = false }: SearchResultItemProps) {
  const getIcon = () => {
    switch (result.type) {
      case 'project':
        return <Folder className="w-5 h-5 text-purple-600" />;
      case 'creative':
        return result.metadata?.fileType?.startsWith('video') 
          ? <FileVideo className="w-5 h-5 text-blue-600" />
          : <FileImage className="w-5 h-5 text-green-600" />;
      case 'analysis':
        return <BarChart3 className="w-5 h-5 text-orange-600" />;
      default:
        return <FileImage className="w-5 h-5 text-gray-600" />;
    }
  };

  const getTypeLabel = () => {
    switch (result.type) {
      case 'project':
        return 'Project';
      case 'creative':
        return 'Creative';
      case 'analysis':
        return 'Analysis';
      default:
        return 'Item';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays - 1} days ago`;
    
    return date.toLocaleDateString();
  };

  const renderThumbnail = () => {
    if (result.thumbnail) {
      return (
        <img
          src={result.thumbnail}
          alt={result.title}
          className="w-10 h-10 rounded-lg object-cover"
        />
      );
    }
    
    return (
      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
        {getIcon()}
      </div>
    );
  };

  const renderMetadata = () => {
    const { metadata } = result;
    
    switch (result.type) {
      case 'project':
        return (
          <div className="flex items-center text-xs text-gray-500 space-x-3">
            {metadata?.memberCount && (
              <span className="flex items-center">
                <Users className="w-3 h-3 mr-1" />
                {metadata.memberCount} member{metadata.memberCount !== 1 ? 's' : ''}
              </span>
            )}
            <span>{formatDate(result.updatedAt)}</span>
          </div>
        );
        
      case 'creative':
        return (
          <div className="flex items-center text-xs text-gray-500 space-x-3">
            {result.score && (
              <span className={`flex items-center ${result.score >= 80 ? 'text-green-600' : result.score >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                <Star className="w-3 h-3 mr-1" />
                {result.score}%
              </span>
            )}
            {metadata?.fileType && (
              <span className="uppercase">
                {metadata.fileType.split('/')[0]}
              </span>
            )}
            <span>{formatDate(result.createdAt)}</span>
          </div>
        );
        
      case 'analysis':
        return (
          <div className="flex items-center text-xs text-gray-500 space-x-3">
            {result.score && (
              <span className={`flex items-center ${result.score >= 80 ? 'text-green-600' : result.score >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                <Star className="w-3 h-3 mr-1" />
                {result.score}%
              </span>
            )}
            {metadata?.platformsAnalyzed && (
              <span>{metadata.platformsAnalyzed} platform{metadata.platformsAnalyzed !== 1 ? 's' : ''}</span>
            )}
            <span>{formatDate(result.createdAt)}</span>
          </div>
        );
        
      default:
        return (
          <span className="text-xs text-gray-500">
            {formatDate(result.updatedAt)}
          </span>
        );
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={`
        flex items-center p-3 cursor-pointer transition-all duration-200
        ${isHighlighted 
          ? 'bg-purple-50 border-l-4 border-purple-500' 
          : 'hover:bg-gray-50'
        }
      `}
      onClick={() => onClick(result)}
    >
      {/* Thumbnail/Icon */}
      <div className="flex-shrink-0 mr-3">
        {renderThumbnail()}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center space-x-2">
            <span className={`
              px-2 py-1 text-xs font-medium rounded-full
              ${result.type === 'project' ? 'bg-purple-100 text-purple-700' : ''}
              ${result.type === 'creative' ? 'bg-blue-100 text-blue-700' : ''}
              ${result.type === 'analysis' ? 'bg-orange-100 text-orange-700' : ''}
            `}>
              {getTypeLabel()}
            </span>
            {result.projectName && result.type !== 'project' && (
              <span className="text-xs text-gray-500">
                in {result.projectName}
              </span>
            )}
          </div>
          <ArrowRight className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        
        <h4 className="font-medium text-gray-900 truncate mb-1">
          {result.title}
        </h4>
        
        {result.description && (
          <p className="text-sm text-gray-600 truncate mb-2">
            {result.description}
          </p>
        )}
        
        {renderMetadata()}
      </div>
    </motion.div>
  );
}